import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FAQ.css';

interface FAQItem {
  question: string;
  answer: string | string[];
}

interface FAQProps {
  sections?: string[]; // Which sections to show, e.g., ['platform', 'syntheticJuror']
  showAll?: boolean;
  compact?: boolean;
}

const FAQ: React.FC<FAQProps> = ({ sections, showAll = false, compact = false }) => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (key: string) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(key)) {
      newOpenItems.delete(key);
    } else {
      newOpenItems.add(key);
    }
    setOpenItems(newOpenItems);
  };

  const allSectionKeys = [
    'platform',
    'syntheticJuror',
    'factFinder',
    'trialSignal',
    'speed',
    'validity',
    'gettingStarted'
  ];

  const sectionsToShow = showAll ? allSectionKeys : (sections || ['platform', 'syntheticJuror']);

  const renderAnswer = (answer: string | string[]) => {
    if (Array.isArray(answer)) {
      return (
        <ul className="faq-answer-list">
          {answer.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }
    return <p>{answer}</p>;
  };

  return (
    <section className={`faq-section ${compact ? 'compact' : ''}`}>
      <div className="faq-container">
        <div className="faq-header">
          <div className="faq-eyebrow" aria-hidden="true">
            <span className="eyebrow-rule" />
            <span className="eyebrow-mark">✦</span>
            <span className="eyebrow-rule" />
          </div>
          <span className="faq-label">{t('faq.label')}</span>
          <h2 className="faq-title">{t('faq.title')}</h2>
          <p className="faq-subtitle">{t('faq.subtitle')}</p>
        </div>

        <div className="faq-content">
          {sectionsToShow.map((sectionKey) => {
            const sectionTitle = t(`faq.sections.${sectionKey}.title`);
            const items = t(`faq.sections.${sectionKey}.items`, { returnObjects: true }) as FAQItem[];

            if (!items || !Array.isArray(items)) return null;

            return (
              <div key={sectionKey} className="faq-category">
                <h3 className="faq-category-title">{sectionTitle}</h3>
                <div className="faq-items">
                  {items.map((item, index) => {
                    const itemKey = `${sectionKey}-${index}`;
                    const isOpen = openItems.has(itemKey);

                    return (
                      <div
                        key={itemKey}
                        className={`faq-item ${isOpen ? 'open' : ''}`}
                      >
                        <button
                          className="faq-question"
                          onClick={() => toggleItem(itemKey)}
                          aria-expanded={isOpen}
                        >
                          <span>{item.question}</span>
                          <span className="faq-icon">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              className={isOpen ? 'rotated' : ''}
                            >
                              <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </button>
                        <div className="faq-answer">
                          <div className="faq-answer-content">
                            {renderAnswer(item.answer)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
